(function () {
  "use strict";

  // The quiz section injects this script per instance; run once per page.
  if (window.__hhQuizLoaded) return;
  window.__hhQuizLoaded = true;

  const DISMISS_KEY = "heathers:quiz-popup-dismissed";
  const root = window.Shopify?.routes?.root || "/";
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  // A single document-level Escape handler drives whichever modal is open,
  // so re-initialising a section never stacks duplicate listeners.
  let escBound = false;
  let activeModal = null;

  function focusEl(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }

  function dispatchQuizEvent(name, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  }

  function initQuiz(scope) {
    const quiz = scope.querySelector ? scope.querySelector("[data-quiz]") : null;
    if (!quiz || quiz.dataset.quizInit === "1") return;
    quiz.dataset.quizInit = "1";

    const collectionHandle = quiz.dataset.collectionHandle || "all";
    const resultsLimit = parseInt(quiz.dataset.resultsLimit, 10) || 3;
    const browseUrl = quiz.dataset.browseUrl || `${root}collections/all`;

    const panels = {
      steps: quiz.querySelector('[data-quiz-panel="steps"]'),
      results: quiz.querySelector('[data-quiz-panel="results"]'),
    };

    const steps = Array.from(quiz.querySelectorAll("[data-quiz-step]"));
    const progressBar = quiz.querySelector("[data-quiz-progress]");
    const stepLabel = quiz.querySelector("[data-quiz-step-label]");
    const resultsGrid = quiz.querySelector("[data-quiz-results]");
    const resultsLoading = quiz.querySelector("[data-quiz-loading]");
    const resultsEmpty = quiz.querySelector("[data-quiz-empty]");
    const widenNote = quiz.querySelector("[data-quiz-widen-note]");
    const seeAllBtn = quiz.querySelector("[data-quiz-see-all]");
    const backBtn = quiz.querySelector("[data-quiz-back]");
    const nav = quiz.querySelector("[data-quiz-nav]");
    const widenCopy = {
      upEmpty: quiz.dataset.widenUpEmpty || "",
      downEmpty: quiz.dataset.widenDownEmpty || "",
      upPartial: quiz.dataset.widenUpPartial || "",
      downPartial: quiz.dataset.widenDownPartial || "",
      outside: quiz.dataset.widenOutside || "",
    };
    const matchLineEl = quiz.querySelector("[data-quiz-match-line]");
    const matchFallback = matchLineEl?.dataset.fallback || matchLineEl?.textContent || "";
    const matchCopy = {
      line: quiz.dataset.matchLine || "",
      join: quiz.dataset.matchJoin || ", ",
      or: quiz.dataset.matchOr || " or ",
      and: quiz.dataset.matchAnd || " and ",
      budgetUnder: quiz.dataset.matchBudgetUnder || "",
      budgetOver: quiz.dataset.matchBudgetOver || "",
      budgetRange: quiz.dataset.matchBudgetRange || "",
    };

    let currentStep = 0;
    let answers = [];
    let started = false;

    function markQuizStarted() {
      if (started) return;
      started = true;
      dispatchQuizEvent("quiz_start", {
        question_count: steps.length,
        collection_handle: collectionHandle,
      });
    }

    function showPanel(name) {
      Object.entries(panels).forEach(([key, panel]) => {
        if (!panel) return;
        const active = key === name;
        panel.hidden = !active;
        panel.classList.toggle("is-active", active);
      });
    }

    function updateProgress() {
      if (!progressBar || !steps.length) return;
      const visible = visibleStepIndexes();
      const pos = Math.max(0, visible.indexOf(currentStep)) + 1;
      const total = visible.length || steps.length;
      progressBar.style.width = `${(pos / total) * 100}%`;
      if (stepLabel) {
        stepLabel.textContent = `Question ${pos} of ${total}`;
      }
    }

    function showStep(index, { focus = false } = {}) {
      steps.forEach((step, i) => {
        step.hidden = i !== index;
        if (i === index && focus) {
          focusEl(step.querySelector(".quiz__question"));
        }
      });
      currentStep = index;
      updateProgress();
      if (nav) nav.hidden = index === 0;
      restoreMultiSelections(steps[index], answers[index]);
    }

    function clearStepOptions(step) {
      step?.querySelectorAll("[data-quiz-option]").forEach((option) => {
        option.setAttribute("aria-checked", "false");
      });
    }

    function syncMultiContinue(step) {
      if (!step?.hasAttribute("data-quiz-multi")) return;
      const continueBtn = step.querySelector("[data-quiz-continue]");
      if (!continueBtn) return;
      const selected = step.querySelectorAll(
        "[data-quiz-multi-option][aria-checked='true']:not([data-quiz-exclusive])"
      );
      continueBtn.disabled = selected.length === 0;
    }

    function collectMultiAnswers(step) {
      return Array.from(
        step.querySelectorAll(
          "[data-quiz-multi-option][aria-checked='true']:not([data-quiz-exclusive])"
        )
      )
        .map(parseAnswer)
        .filter((answer) => answer.value);
    }

    function persistMultiDraft(step, index) {
      if (!step?.hasAttribute("data-quiz-multi")) return;
      const selected = collectMultiAnswers(step);
      answers[index] = selected.length ? selected : null;
    }

    function restoreMultiSelections(step, stored) {
      if (!step?.hasAttribute("data-quiz-multi")) return;
      clearStepOptions(step);
      if (!Array.isArray(stored) || !stored.length) {
        syncMultiContinue(step);
        return;
      }
      stored.forEach((answer) => {
        if (answer?.type !== "metafield" || !answer.value) return;
        const value = answer.value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        const match = step.querySelector(
          `[data-quiz-multi-option][data-answer-value="${value}"]`
        );
        if (match) match.setAttribute("aria-checked", "true");
      });
      syncMultiContinue(step);
    }

    function resetQuiz() {
      currentStep = 0;
      answers = [];
      started = false;
      showPanel("steps");
      showStep(0);
      if (resultsGrid) resultsGrid.innerHTML = "";
      if (resultsEmpty) resultsEmpty.hidden = true;
      if (widenNote) {
        widenNote.hidden = true;
        widenNote.textContent = "";
      }
      if (seeAllBtn) seeAllBtn.hidden = true;
      if (resultsLoading) resultsLoading.hidden = true;
      setMatchLine("");
      steps.forEach((step) => {
        clearStepOptions(step);
        syncMultiContinue(step);
      });
    }

    function parseAnswer(btn) {
      if (btn.dataset.answerType === "price") {
        const min = btn.dataset.priceMin ? parseInt(btn.dataset.priceMin, 10) : null;
        const max = btn.dataset.priceMax ? parseInt(btn.dataset.priceMax, 10) : null;
        return { type: "price", min, max };
      }
      // Legacy preference answers (pre-custom.for filter) — mapped in getMetafields.
      if (btn.dataset.answerType === "preference") {
        return {
          type: "preference",
          value: btn.dataset.answerValue || "",
        };
      }
      // Metafield answers narrow via Shopify's native product-metafield filter
      // (filter.p.m.<namespace>.<key>=<value>), e.g. custom.sensation=Clitoral.
      // Blank value = "show all" for that facet (e.g. sensation / shopping-for not sure).
      return {
        type: "metafield",
        key: btn.dataset.answerKey || "",
        value: btn.dataset.answerValue || "",
      };
    }

    const FOR_KEY = "custom.for";
    const EXPERIENCE_KEY = "custom.experience";
    const SENSATION_KEY = "custom.sensation";

    // Map quiz/legacy labels onto locked custom.for choices (Gift, Couples, Me).
    // Blank / "any" skips the For filter.
    const FOR_VALUE_ALIASES = {
      solo: "Me",
      me: "Me",
      partner: "Couples",
      couples: "Couples",
      gift: "Gift",
      any: "",
    };

    const FOR_ANALYTICS = {
      Me: "solo",
      Couples: "partner",
      Gift: "gift",
    };

    function normalizeForValue(raw) {
      if (!raw) return "";
      const aliased = FOR_VALUE_ALIASES[String(raw).toLowerCase()];
      if (aliased !== undefined) return aliased;
      return raw;
    }

    function stepIndexForKey(key) {
      return steps.findIndex((step) =>
        Boolean(step.querySelector(`[data-answer-key="${key}"]`))
      );
    }

    function shoppingForValue(answer) {
      if (!answer) return "";
      if (answer.type === "preference") return normalizeForValue(answer.value);
      if (answer.type === "metafield" && answer.key === FOR_KEY) {
        return normalizeForValue(answer.value);
      }
      return "";
    }

    function isGiftPath() {
      return shoppingForValue(answers[0]) === "Gift";
    }

    function giftSkippedStepIndexes() {
      if (!isGiftPath()) return [];
      return [stepIndexForKey(EXPERIENCE_KEY), stepIndexForKey(SENSATION_KEY)].filter(
        (index) => index >= 0
      );
    }

    function visibleStepIndexes() {
      const skipped = new Set(giftSkippedStepIndexes());
      return steps.map((_, index) => index).filter((index) => !skipped.has(index));
    }

    function nextVisibleStep(from) {
      return visibleStepIndexes().find((index) => index > from) ?? null;
    }

    function prevVisibleStep(from) {
      const visible = visibleStepIndexes();
      for (let i = visible.length - 1; i >= 0; i -= 1) {
        if (visible[i] < from) return visible[i];
      }
      return null;
    }

    function clearStepAnswer(index) {
      if (index < 0) return;
      answers[index] = null;
      clearStepOptions(steps[index]);
      syncMultiContinue(steps[index]);
    }

    function clearGiftSkippedAnswers() {
      giftSkippedStepIndexes().forEach(clearStepAnswer);
    }

    function flattenAnswers(selections) {
      return selections.flatMap((item) => {
        if (Array.isArray(item)) return item;
        return item ? [item] : [];
      });
    }

    function getMetafields(selections) {
      return flattenAnswers(selections)
        .flatMap((item) => {
          if (item?.type === "preference") {
            const value = normalizeForValue(item.value);
            if (!value) return [];
            return [{ type: "metafield", key: FOR_KEY, value }];
          }
          if (item?.type !== "metafield" || !item.key) return [];
          if (item.key === FOR_KEY) {
            const value = normalizeForValue(item.value);
            if (!value) return [];
            return [{ type: "metafield", key: FOR_KEY, value }];
          }
          if (!item.value) return [];
          return [item];
        });
    }

    function getPriceRange(selections) {
      return flattenAnswers(selections).find((item) => item?.type === "price") || null;
    }

    // Beginner = Beginner only. Intermediate = Beginner + Advanced.
    // Advanced = Advanced only. Intermediate is quiz-only (not a product metafield choice).
    function splitExperience(metafields) {
      const experience = metafields.find((mf) => mf.key === EXPERIENCE_KEY) || null;
      const other = metafields.filter((mf) => mf.key !== EXPERIENCE_KEY);
      return { experience, other };
    }

    function splitSensations(metafields) {
      const sensations = metafields.filter((mf) => mf.key === SENSATION_KEY);
      const other = metafields.filter((mf) => mf.key !== SENSATION_KEY);
      return { sensations, other };
    }

    function answerLabel(answer) {
      if (!answer) return "";
      if (Array.isArray(answer)) {
        return answer.map(answerLabel).filter(Boolean).join("|") || `${SENSATION_KEY}:any`;
      }
      if (answer.type === "preference") {
        const value = normalizeForValue(answer.value);
        return value ? `shopping:${FOR_ANALYTICS[value] || value.toLowerCase()}` : "shopping:any";
      }
      if (answer.type === "metafield") {
        if (answer.key === FOR_KEY) {
          const value = normalizeForValue(answer.value);
          return value ? `shopping:${FOR_ANALYTICS[value] || value.toLowerCase()}` : "shopping:any";
        }
        return answer.value || `${answer.key}:any`;
      }
      const min = answer.min != null ? answer.min / 100 : "";
      const max = answer.max != null ? answer.max / 100 : "";
      return `price:${min}-${max}`;
    }

    function mfParam(mf) {
      // filter.p.m.<namespace>.<key> — key already stored as "namespace.key".
      return `filter.p.m.${mf.key}`;
    }

    function buildFilterUrl(metafields, priceRange, extraParams) {
      const base = `${root}collections/${collectionHandle}`;
      const params = new URLSearchParams();

      metafields.forEach((mf) => params.append(mfParam(mf), mf.value));

      if (priceRange) {
        if (priceRange.min != null) params.append("filter.v.price.gte", String(priceRange.min / 100));
        if (priceRange.max != null) params.append("filter.v.price.lte", String(priceRange.max / 100));
      }

      params.set("filter.v.availability", "1");
      params.set("view", "product-cards");
      if (extraParams?.sort) params.set("sort_by", extraParams.sort);

      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    // Browse link for shoppers: filter params use major currency units (not cents).
    // Default to in-stock (same as nav collection links).
    // Intermediate omits the experience filter so Beginner + Advanced both appear.
    // Multiple sensations are appended as repeated filter params (OR in Search & Discovery).
    function buildBrowseUrl(selections, { omitPrice = false } = {}) {
      const metafields = getMetafields(selections);
      const priceRange = omitPrice ? null : getPriceRange(selections);
      const { experience, other } = splitExperience(metafields);
      const browseMfs =
        experience?.value === "Intermediate"
          ? other
          : experience
            ? [...other, experience]
            : other;
      const base = `${root}collections/${collectionHandle}`;
      const params = new URLSearchParams();

      browseMfs.forEach((mf) => params.append(mfParam(mf), mf.value));

      if (priceRange) {
        if (priceRange.min != null) params.append("filter.v.price.gte", String(priceRange.min / 100));
        if (priceRange.max != null) params.append("filter.v.price.lte", String(priceRange.max / 100));
      }

      params.set("filter.v.availability", "1");

      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    function isAvailable(card) {
      return card.dataset.productAvailable !== "false";
    }

    function cardKey(card) {
      return card.getAttribute("href") || card.dataset.productPrice || "";
    }

    function mergeUnique(primary, secondary) {
      const seen = new Set();
      const out = [];
      [...primary, ...secondary].forEach((card) => {
        const key = cardKey(card);
        if (key && seen.has(key)) return;
        if (key) seen.add(key);
        out.push(card);
      });
      return out;
    }

    function mergeMany(cardSets) {
      return cardSets.reduce((acc, cards) => mergeUnique(acc, cards), []);
    }

    function cardMatchesPrice(card, priceRange) {
      if (!priceRange) return true;
      const cents = parseInt(card.dataset.productPrice, 10);
      if (Number.isNaN(cents)) return true;
      if (priceRange.min != null && cents < priceRange.min) return false;
      if (priceRange.max != null && cents > priceRange.max) return false;
      return true;
    }

    function filterCards(cards, priceRange) {
      if (!priceRange) return cards;
      return cards.filter((card) => cardMatchesPrice(card, priceRange));
    }

    async function fetchCardsFromUrl(url, priceRange) {
      const res = await fetch(url);
      if (!res.ok) return [];
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const cards = Array.from(doc.querySelectorAll(".product-card")).filter(isAvailable);
      return filterCards(cards, priceRange);
    }

    // Try filtered URL with price, then metafields-only (still client-price-filtered).
    async function fetchWithFallback(metafields, priceRange, extraParams) {
      const urls = [];
      if (metafields.length || priceRange) {
        urls.push(buildFilterUrl(metafields, priceRange, extraParams));
      }
      if (metafields.length && priceRange) {
        urls.push(buildFilterUrl(metafields, null, extraParams));
      }
      if (!urls.length) {
        urls.push(buildFilterUrl([], null, extraParams));
      }

      for (const url of urls) {
        try {
          const cards = await fetchCardsFromUrl(url, priceRange);
          if (cards.length) return cards;
        } catch (err) {
          console.error("Quiz results fetch failed:", err);
        }
      }
      return [];
    }

    async function fetchForMetafields(metafields, priceRange, extraParams) {
      const { experience, other } = splitExperience(metafields);

      // Intermediate: show Beginner and Advanced (Beginner-first merge).
      if (experience?.value === "Intermediate") {
        const beginnerMf = { type: "metafield", key: EXPERIENCE_KEY, value: "Beginner" };
        const advancedMf = { type: "metafield", key: EXPERIENCE_KEY, value: "Advanced" };
        const [beginnerCards, advancedCards] = await Promise.all([
          fetchWithFallback([...other, beginnerMf], priceRange, extraParams),
          fetchWithFallback([...other, advancedMf], priceRange, extraParams),
        ]);
        return mergeUnique(beginnerCards, advancedCards);
      }

      // Beginner / Advanced / no experience: exclusive filter as selected.
      // Drop Intermediate from the filter URL if it somehow remains.
      const filterMfs = metafields.filter(
        (mf) => !(mf.key === EXPERIENCE_KEY && mf.value === "Intermediate")
      );
      return fetchWithFallback(filterMfs, priceRange, extraParams);
    }

    async function fetchProductCards(selections, priceOverride, extraParams) {
      const metafields = getMetafields(selections);
      const priceRange = priceOverride === undefined ? getPriceRange(selections) : priceOverride;
      const { experience, other } = splitExperience(metafields);
      const { sensations, other: rest } = splitSensations(other);

      // Multiple sensations: OR-merge (one fetch per sensation) so results include
      // products tagged with any of the chosen sensations.
      if (sensations.length > 1) {
        const base = experience ? [...rest, experience] : rest;
        const cardSets = await Promise.all(
          sensations.map((sensation) =>
            fetchForMetafields([...base, sensation], priceRange, extraParams)
          )
        );
        return mergeMany(cardSets);
      }

      return fetchForMetafields(metafields, priceRange, extraParams);
    }

    function cardPriceCents(card) {
      const cents = parseInt(card.dataset.productPrice, 10);
      return Number.isNaN(cents) ? null : cents;
    }

    function distanceToBand(card, priceRange) {
      const cents = cardPriceCents(card);
      if (cents == null) return Number.POSITIVE_INFINITY;
      if (priceRange.max != null && cents > priceRange.max) return cents - priceRange.max;
      if (priceRange.min != null && cents < priceRange.min) return priceRange.min - cents;
      return 0;
    }

    function extraDirection(cards, priceRange) {
      let above = 0;
      let below = 0;
      cards.forEach((card) => {
        const cents = cardPriceCents(card);
        if (cents == null) return;
        if (priceRange.max != null && cents > priceRange.max) above += 1;
        else if (priceRange.min != null && cents < priceRange.min) below += 1;
      });
      if (above && !below) return "up";
      if (below && !above) return "down";
      return "mixed";
    }

    function formatRand(cents) {
      if (cents == null) return "";
      return `R${Math.round(cents / 100)}`;
    }

    function fillWidenTemplate(template, amount) {
      if (!template) return "";
      return template.replace(/\{amount\}/g, amount || "");
    }

    function widenNoteText(exactCount, direction, priceRange) {
      if (direction === "up") {
        const amount = formatRand(priceRange.max);
        const template = exactCount ? widenCopy.upPartial : widenCopy.upEmpty;
        return fillWidenTemplate(template, amount);
      }
      if (direction === "down") {
        const amount = formatRand(priceRange.min);
        const template = exactCount ? widenCopy.downPartial : widenCopy.downEmpty;
        return fillWidenTemplate(template, amount);
      }
      return widenCopy.outside || "";
    }

    function setWidenNote(text) {
      if (!widenNote) return;
      if (!text) {
        widenNote.hidden = true;
        widenNote.textContent = "";
        return;
      }
      widenNote.textContent = text;
      widenNote.hidden = false;
    }

    function joinWith(separator, fallback) {
      const raw = separator || fallback;
      if (!raw.trim()) return fallback;
      if (raw !== raw.trim()) return raw;
      if (raw === "," || raw === ";") return `${raw} `;
      return ` ${raw} `;
    }

    function budgetFilterLabel(priceRange) {
      if (!priceRange) return "";
      const min = priceRange.min != null ? formatRand(priceRange.min) : "";
      const max = priceRange.max != null ? formatRand(priceRange.max) : "";
      if (priceRange.min != null && priceRange.max != null) {
        return (matchCopy.budgetRange || "{min}-{max}")
          .replace(/\{min\}/g, min)
          .replace(/\{max\}/g, max);
      }
      if (priceRange.max != null) {
        return (matchCopy.budgetUnder || "").replace(/\{amount\}/g, max);
      }
      if (priceRange.min != null) {
        return (matchCopy.budgetOver || "").replace(/\{amount\}/g, min);
      }
      return "";
    }

    // Name the filters this run actually applied. Product cards only expose
    // price, so a shared line above the grid is the honest version. Omit
    // budget when widening already explains it.
    function appliedFilterLabels(selections, { omitBudget = false } = {}) {
      const parts = [];
      const metafields = getMetafields(selections);
      const forMf = metafields.find((mf) => mf.key === FOR_KEY);
      const { experience } = splitExperience(metafields);
      const { sensations } = splitSensations(metafields);
      const orSep = joinWith(matchCopy.or, " or ");
      const andSep = joinWith(matchCopy.and, " and ");

      if (forMf?.value) parts.push(forMf.value);

      if (experience?.value === "Intermediate") {
        parts.push(["Beginner", "Advanced"].join(andSep));
      } else if (experience?.value) {
        parts.push(experience.value);
      }

      if (sensations.length) {
        parts.push(sensations.map((mf) => mf.value).join(orSep));
      }

      if (!omitBudget) {
        const budget = budgetFilterLabel(getPriceRange(selections));
        if (budget) parts.push(budget);
      }

      return parts;
    }

    function matchLineText(selections, { omitBudget = false } = {}) {
      if (!matchCopy.line) return "";
      const filters = appliedFilterLabels(selections, { omitBudget });
      if (!filters.length) return "";
      return matchCopy.line.replace(
        /\{filters\}/g,
        filters.join(joinWith(matchCopy.join, ", "))
      );
    }

    function setMatchLine(text) {
      if (!matchLineEl) return;
      const next = text || matchFallback;
      matchLineEl.textContent = next;
      matchLineEl.hidden = !next;
    }

    // Keep For / Experience / Sensation. If the budget band is thin, fill from
    // the next band(s) and put exact matches first.
    async function padByBudget(exact, selections) {
      const priceRange = getPriceRange(selections);
      if (!priceRange || exact.length >= resultsLimit) {
        return { cards: exact.slice(0, resultsLimit), extras: [], direction: null };
      }

      const adjacent = [];
      if (priceRange.max != null) {
        adjacent.push({
          range: { min: priceRange.max, max: null },
          sort: "price-ascending",
        });
      }
      if (priceRange.min != null) {
        adjacent.push({
          range: { min: null, max: priceRange.min },
          sort: "price-descending",
        });
      }
      if (!adjacent.length) {
        return { cards: exact.slice(0, resultsLimit), extras: [], direction: null };
      }

      const pools = await Promise.all(
        adjacent.map((item) => fetchProductCards(selections, item.range, { sort: item.sort }))
      );

      const seen = new Set(exact.map(cardKey).filter(Boolean));
      const missedExact = [];
      const outside = [];
      pools.flat().forEach((card) => {
        const key = cardKey(card);
        if (!key || seen.has(key)) return;
        seen.add(key);
        if (cardMatchesPrice(card, priceRange)) missedExact.push(card);
        else outside.push(card);
      });

      const allExact = mergeUnique(exact, missedExact);
      outside.sort((a, b) => distanceToBand(a, priceRange) - distanceToBand(b, priceRange));

      const exactShown = allExact.slice(0, resultsLimit);
      const extrasShown = outside.slice(0, Math.max(0, resultsLimit - exactShown.length));
      return {
        cards: [...exactShown, ...extrasShown],
        extras: extrasShown,
        direction: extrasShown.length ? extraDirection(extrasShown, priceRange) : null,
        exactCount: exactShown.length,
        priceRange,
      };
    }

    async function showResults() {
      showPanel("results");
      const heading = panels.results?.querySelector(".quiz__results-heading");
      focusEl(heading);

      if (resultsGrid) resultsGrid.innerHTML = "";
      if (resultsEmpty) resultsEmpty.hidden = true;
      setWidenNote("");
      if (seeAllBtn) seeAllBtn.hidden = true;
      if (resultsLoading) resultsLoading.hidden = false;

      const selectedLabels = answers
        .filter(Boolean)
        .map((answer) => answerLabel(answer))
        .filter(Boolean);
      const exact = await fetchProductCards(answers);
      const padded = await padByBudget(exact, answers);
      const limited = padded.cards.slice(0, resultsLimit);
      const widened = padded.extras.length > 0;
      const resultsBrowse = buildBrowseUrl(answers, { omitPrice: widened });

      if (resultsLoading) resultsLoading.hidden = true;

      if (!limited.length) {
        setMatchLine("");
        if (resultsEmpty) resultsEmpty.hidden = false;
        dispatchQuizEvent("quiz_complete", {
          selected_tags: selectedLabels.join(","),
          results_count: 0,
          browse_url: browseUrl,
          budget_widened: false,
        });
        return;
      }

      if (widened) {
        setWidenNote(widenNoteText(padded.exactCount, padded.direction, padded.priceRange));
      }

      setMatchLine(matchLineText(answers, { omitBudget: widened }));

      limited.forEach((card) => {
        resultsGrid?.appendChild(card.cloneNode(true));
      });

      if (seeAllBtn) {
        seeAllBtn.href = resultsBrowse;
        seeAllBtn.hidden = false;
      }

      dispatchQuizEvent("quiz_complete", {
        selected_tags: selectedLabels.join(","),
        results_count: limited.length,
        browse_url: resultsBrowse,
        budget_widened: widened,
        widen_direction: padded.direction || "",
      });
    }

    function advanceFromStep(answer, skipped) {
      markQuizStarted();

      answers[currentStep] = skipped ? null : answer;

      if (currentStep === 0) {
        if (shoppingForValue(answer) === "Gift") clearGiftSkippedAnswers();
      }

      if (!skipped && answer) {
        dispatchQuizEvent("quiz_step", {
          step_index: currentStep + 1,
          answer_tag: answerLabel(answer),
        });
      } else if (skipped) {
        dispatchQuizEvent("quiz_skip", {
          step_index: currentStep + 1,
          skipped_all: false,
        });
      }

      const next = nextVisibleStep(currentStep);
      if (next != null) {
        showStep(next, { focus: true });
        return;
      }

      showResults();
    }

    function toggleMultiOption(btn) {
      const step = btn.closest("[data-quiz-step]");
      if (!step) return;
      const stepIndex = steps.indexOf(step);

      // Blank-value exclusive option (e.g. "I am not sure"): one tap skips the filter.
      if (btn.hasAttribute("data-quiz-exclusive")) {
        clearStepOptions(step);
        btn.setAttribute("aria-checked", "true");
        syncMultiContinue(step);
        advanceFromStep(null, true);
        return;
      }

      const checked = btn.getAttribute("aria-checked") === "true";
      // Selecting a valued option clears the exclusive "not sure" choice.
      step.querySelectorAll("[data-quiz-exclusive]").forEach((option) => {
        option.setAttribute("aria-checked", "false");
      });
      btn.setAttribute("aria-checked", checked ? "false" : "true");
      persistMultiDraft(step, stepIndex);
      syncMultiContinue(step);
    }

    steps.forEach((step) => {
      const group = step.querySelector(".quiz__options");
      if (!group) return;
      group.addEventListener("keydown", (e) => {
        const options = Array.from(group.querySelectorAll("[data-quiz-option]"));
        const current = options.indexOf(e.target.closest("[data-quiz-option]"));
        if (current < 0) return;

        let nextIndex = -1;
        if (e.key === "ArrowDown" || e.key === "ArrowRight") {
          nextIndex = (current + 1) % options.length;
        } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
          nextIndex = (current - 1 + options.length) % options.length;
        }
        if (nextIndex < 0) return;

        e.preventDefault();
        options[nextIndex].focus();
      });
    });

    quiz.querySelectorAll("[data-quiz-option]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.hasAttribute("data-quiz-multi-option")) {
          toggleMultiOption(btn);
          return;
        }

        const step = btn.closest("[data-quiz-step]");
        step?.querySelectorAll("[data-quiz-option]").forEach((option) => {
          option.setAttribute("aria-checked", "false");
        });
        btn.setAttribute("aria-checked", "true");
        advanceFromStep(parseAnswer(btn), false);
      });
    });

    quiz.querySelectorAll("[data-quiz-continue]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const step = btn.closest("[data-quiz-step]");
        if (!step) return;
        const selected = collectMultiAnswers(step);
        if (!selected.length) return;
        advanceFromStep(selected, false);
      });
    });

    quiz.querySelectorAll("[data-quiz-not-sure]").forEach((btn) => {
      btn.addEventListener("click", () => {
        advanceFromStep(null, true);
      });
    });

    backBtn?.addEventListener("click", () => {
      const prev = prevVisibleStep(currentStep);
      if (prev == null) return;
      persistMultiDraft(steps[currentStep], currentStep);
      showStep(prev, { focus: true });
    });

    quiz.querySelector("[data-quiz-restart]")?.addEventListener("click", resetQuiz);

    resultsGrid?.addEventListener("click", (e) => {
      const card = e.target.closest(".product-card");
      if (!card) return;
      dispatchQuizEvent("quiz_result_click", {
        product_title: card.querySelector(".product-card__title")?.textContent?.trim() || "",
        product_url: card.getAttribute("href") || "",
      });
    });

    showPanel("steps");
    showStep(0);
  }

  function initModal(scope) {
    const modal = scope.querySelector ? scope.querySelector("[data-quiz-modal]") : null;
    if (!modal || modal.dataset.quizModalInit === "1") return;
    modal.dataset.quizModalInit = "1";

    const dialog = modal.querySelector(".quiz-modal__dialog");
    const trigger =
      Math.min(Math.max(parseInt(modal.dataset.scrollTrigger, 10) || 40, 0), 100) / 100;

    let opened = false;
    let lastFocused = null;

    function alreadyDismissed() {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === "1";
      } catch {
        return false;
      }
    }

    function markDismissed() {
      try {
        sessionStorage.setItem(DISMISS_KEY, "1");
      } catch {
        /* sessionStorage unavailable — fail open */
      }
    }

    function openModal() {
      if (opened) return;
      opened = true;
      lastFocused = document.activeElement;
      modal.hidden = false;
      // Force reflow so the transition runs from the hidden state.
      void modal.offsetWidth;
      modal.classList.add("is-open");
      document.body.classList.add("quiz-modal-open");
      focusEl(dialog?.querySelector(".quiz-modal__close"));
      dispatchQuizEvent("quiz_popup_open", { scroll_trigger: trigger });
    }

    function closeModal() {
      if (!opened) return;
      opened = false;
      modal.classList.remove("is-open");
      document.body.classList.remove("quiz-modal-open");
      modal.hidden = true;
      if (!designMode) markDismissed();
      if (lastFocused && typeof lastFocused.focus === "function") {
        focusEl(lastFocused);
      }
    }

    // Attach dismiss controls first so they exist in every mode (including the
    // theme editor, where the popup opens immediately).
    modal.querySelectorAll("[data-quiz-modal-dismiss]").forEach((el) => {
      el.addEventListener("click", closeModal);
    });

    activeModal = { close: closeModal, isOpen: () => opened };

    if (!escBound) {
      escBound = true;
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && activeModal?.isOpen()) activeModal.close();
      });
    }

    // In the theme editor, show the popup immediately so it can be edited.
    if (designMode) {
      openModal();
      return;
    }

    if (alreadyDismissed()) return;

    function scrolledPastTrigger() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return true;
      return window.scrollY / scrollable >= trigger;
    }

    function onScroll() {
      // Self-heal if the section was re-rendered and this modal detached.
      if (!modal.isConnected) {
        window.removeEventListener("scroll", onScroll);
        return;
      }
      if (opened) return;
      if (scrolledPastTrigger()) {
        openModal();
        window.removeEventListener("scroll", onScroll);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    // Handle the case where the page is already scrolled on load.
    onScroll();
  }

  function initAll(scope) {
    initQuiz(scope);
    initModal(scope);
  }

  initAll(document);

  // The theme editor swaps section HTML via AJAX without re-running scripts,
  // so re-initialise the affected section on load/select and clean up on unload.
  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
  document.addEventListener("shopify:section:unload", () => {
    document.body.classList.remove("quiz-modal-open");
    activeModal = null;
  });
})();
